import React from 'react';
import { useSelector } from 'react-redux';
import {
  Section,
  SectionWithTitle,
  SectionHeader,
  SecondarySectionTitle,
  AppContent,
  ErrorBoundary,
  Ad,
  baseUnit,
  MEDIA_BREAKPOINTS,
  OneTwoOnTopFullWidth,
  ThreeThreeVC,
  TwoThirdsOneThirdWrapper,
  SixHCForTwoThirds,
  OneOnTopThreeHorizontalArticleList,
  FourVCOneOTC,
  OneOTCFourVC,
  ThreeHCOneOTC,
  MainWrapperForHeaderWithSubMenus,
  NineHCForOneThird,
} from 'mm-ui-components';
import { Store } from 'redux';
import { css, StyleSheet } from 'aphrodite/no-important';
import { Theme } from '../../components/connectedComponents/Theme';
import { MetaTags } from '../../components/connectedComponents/MetaTags';
import { HeadScripts } from '../../components/connectedComponents/HeadScripts';
import { SECTION_NAMES, SLOT_NAMES } from '../templates.const';
import { Page } from '../Page';
import {
  getSectionArticlesByKey,
  getSectionDisplayNameByKey,
  getSectionLinkByKey,
  getSectionTextByKey,
} from '../../store/template/homePage90min/homePage90min.selectors';
import { FooterConnected } from '../../components/connectedComponents/FooterConnected';
import { getAdSlotId } from '../../store/config/config.selectors';
import { ErrorReporter } from '../../errorReporting/errorReporter.type';
import { AppActions, AppState } from '../../store/initialStateRegistration';
import { useMeasureHydrateTime } from '../useMeasureHydrateTime';
import { HeaderWithSubMenusConnected } from '../../components/connectedComponents/HeaderConnected';

interface HomePage90minProps {
  store: Store<AppState, AppActions>;
  errorProvider: ErrorReporter;
}

const getArticlesInTemplate = (state: AppState) => {
  const topSectionArticles = getSectionArticlesByKey('topSection')(state) || [];
  const cardsSectionArticles = getSectionArticlesByKey('cardsSection')(state) || [];
  const contentSection1Articles = getSectionArticlesByKey('contentSection1')(state) || [];
  const contentSection2Articles = getSectionArticlesByKey('contentSection2')(state) || [];
  const contentSection3Articles = getSectionArticlesByKey('contentSection3')(state) || [];
  return [
    ...topSectionArticles,
    ...cardsSectionArticles,
    ...contentSection1Articles,
    ...contentSection2Articles,
    ...contentSection3Articles,
  ];
};

export const HomePage90minComponent: React.FunctionComponent<HomePage90minProps> = ({ store, errorProvider }) => {
  useMeasureHydrateTime();
  return (
    <Page store={store} errorProvider={errorProvider}>
      <ErrorBoundary>
        <HomePage90minContentConnected />
      </ErrorBoundary>
    </Page>
  );
};

const createStyle = () => StyleSheet.create({
  sectionsWrapper: {
    [MEDIA_BREAKPOINTS.large]: {
      gridRowGap: `${baseUnit * 3}px`,
    },
    [MEDIA_BREAKPOINTS.medium]: {
      gridRowGap: `${baseUnit * 2}px`,
    },
    [MEDIA_BREAKPOINTS.small]: {
      gridRowGap: `${baseUnit * 2}px`,
    },
    display: 'grid',
    gridTemplateColumns: '1fr',
    marginTop: `${baseUnit * 0.25}px`,
    marginBottom: `${baseUnit * 0.5}px`,
    justifyItems: 'center',
  },
});

export const HomePage90minContentConnected: React.FunctionComponent = () => {
  const topSectionArticles = useSelector(getSectionArticlesByKey(SECTION_NAMES.TOP_SECTION));
  const cardsSectionDisplayName = useSelector(getSectionDisplayNameByKey(SECTION_NAMES.CARDS_SECTION));
  const cardsSectionArticles = useSelector(getSectionArticlesByKey(SECTION_NAMES.CARDS_SECTION));
  const cardsSectionLink = useSelector(getSectionLinkByKey(SECTION_NAMES.CARDS_SECTION));
  const cardsSectionText = useSelector(getSectionTextByKey(SECTION_NAMES.CARDS_SECTION));
  const contentSection1DisplayName = useSelector(getSectionDisplayNameByKey(SECTION_NAMES.CONTENT_SECTION_1));
  const contentSection1Articles = useSelector(getSectionArticlesByKey(SECTION_NAMES.CONTENT_SECTION_1));
  const contentSection1Link = useSelector(getSectionLinkByKey(SECTION_NAMES.CONTENT_SECTION_1));
  const contentSection1Text = useSelector(getSectionTextByKey(SECTION_NAMES.CONTENT_SECTION_1));
  const contentSection2DisplayName = useSelector(getSectionDisplayNameByKey(SECTION_NAMES.CONTENT_SECTION_2));
  const contentSection2Articles = useSelector(getSectionArticlesByKey(SECTION_NAMES.CONTENT_SECTION_2));
  const contentSection2Link = useSelector(getSectionLinkByKey(SECTION_NAMES.CONTENT_SECTION_2));
  const contentSection2Text = useSelector(getSectionTextByKey(SECTION_NAMES.CONTENT_SECTION_2));
  const contentSection3DisplayName = useSelector(getSectionDisplayNameByKey(SECTION_NAMES.CONTENT_SECTION_3));
  const contentSection3Articles = useSelector(getSectionArticlesByKey(SECTION_NAMES.CONTENT_SECTION_3));
  const contentSection3Link = useSelector(getSectionLinkByKey(SECTION_NAMES.CONTENT_SECTION_3));
  const contentSection3Text = useSelector(getSectionTextByKey(SECTION_NAMES.CONTENT_SECTION_3));
  const contentSection4DisplayName = useSelector(getSectionDisplayNameByKey(SECTION_NAMES.CONTENT_SECTION_4));
  const contentSection4Articles = useSelector(getSectionArticlesByKey(SECTION_NAMES.CONTENT_SECTION_4));
  const contentSection4Link = useSelector(getSectionLinkByKey(SECTION_NAMES.CONTENT_SECTION_4));
  const contentSection4Text = useSelector(getSectionTextByKey(SECTION_NAMES.CONTENT_SECTION_4));
  const contentSection5DisplayName = useSelector(getSectionDisplayNameByKey(SECTION_NAMES.CONTENT_SECTION_5));
  const contentSection5Articles = useSelector(getSectionArticlesByKey(SECTION_NAMES.CONTENT_SECTION_5));
  const contentSection5Link = useSelector(getSectionLinkByKey(SECTION_NAMES.CONTENT_SECTION_5));
  const contentSection5Text = useSelector(getSectionTextByKey(SECTION_NAMES.CONTENT_SECTION_5));
  const contentSection6DisplayName = useSelector(getSectionDisplayNameByKey(SECTION_NAMES.CONTENT_SECTION_6));
  const contentSection6Articles = useSelector(getSectionArticlesByKey(SECTION_NAMES.CONTENT_SECTION_6));
  const contentSection6Link = useSelector(getSectionLinkByKey(SECTION_NAMES.CONTENT_SECTION_6));
  const contentSection6Text = useSelector(getSectionTextByKey(SECTION_NAMES.CONTENT_SECTION_6));
  const contentSection7DisplayName = useSelector(getSectionDisplayNameByKey(SECTION_NAMES.CONTENT_SECTION_7));
  const contentSection7Articles = useSelector(getSectionArticlesByKey(SECTION_NAMES.CONTENT_SECTION_7));
  const contentSection7Link = useSelector(getSectionLinkByKey(SECTION_NAMES.CONTENT_SECTION_7));
  const contentSection7Text = useSelector(getSectionTextByKey(SECTION_NAMES.CONTENT_SECTION_7));
  const articles = useSelector(getArticlesInTemplate);
  const topAdId = useSelector(getAdSlotId(SLOT_NAMES.TOP_AD));
  const oneOnOneId = useSelector(getAdSlotId(SLOT_NAMES.ONE_ON_ONE));
  const style = createStyle();

  return (
    <React.Fragment>
      <MetaTags articles={articles} />
      <HeadScripts />
      <Theme>
        <AppContent>
          <HeaderWithSubMenusConnected />
          <MainWrapperForHeaderWithSubMenus>
            <Ad id={topAdId} />
            <div className={css(style.sectionsWrapper)}>

              <Section themeConfigurationPropKey="firstSectionMaxWidth">
                <OneTwoOnTopFullWidth cards={topSectionArticles} />
              </Section>

              <SectionWithTitle>
                <SectionHeader linkText={cardsSectionText} href={cardsSectionLink}>
                  <SecondarySectionTitle>
                    {cardsSectionDisplayName}
                  </SecondarySectionTitle>
                </SectionHeader>
                <FourVCOneOTC cards={cardsSectionArticles} />
              </SectionWithTitle>

              <SectionWithTitle>
                <SectionHeader linkText={contentSection1Text} href={contentSection1Link}>
                  <SecondarySectionTitle>
                    {contentSection1DisplayName}
                  </SecondarySectionTitle>
                </SectionHeader>
                <OneOTCFourVC cards={contentSection1Articles} />
              </SectionWithTitle>

              <SectionWithTitle>
                <SectionHeader linkText={contentSection2Text} href={contentSection2Link}>
                  <SecondarySectionTitle>
                    {contentSection2DisplayName}
                  </SecondarySectionTitle>
                </SectionHeader>
                <ThreeThreeVC cards={contentSection2Articles} />
              </SectionWithTitle>

              <TwoThirdsOneThirdWrapper>
                <SectionWithTitle>
                  <SectionHeader linkText={contentSection3Text} href={contentSection3Link} paddingFactors={{ large: 0, medium: 0, small: 0 }}>
                    <SecondarySectionTitle>
                      {contentSection3DisplayName}
                    </SecondarySectionTitle>
                  </SectionHeader>
                  <SixHCForTwoThirds cards={contentSection3Articles} />
                </SectionWithTitle>

                <SectionWithTitle>
                  <SectionHeader linkText={contentSection4Text} href={contentSection4Link} paddingFactors={{ large: 0, medium: 0, small: 0 }}>
                    <SecondarySectionTitle>
                      {contentSection4DisplayName}
                    </SecondarySectionTitle>
                  </SectionHeader>
                  <NineHCForOneThird
                    cards={contentSection4Articles}
                  />
                </SectionWithTitle>
              </TwoThirdsOneThirdWrapper>

              <SectionWithTitle>
                <SectionHeader linkText={contentSection5Text} href={contentSection5Link}>
                  <SecondarySectionTitle>
                    {contentSection5DisplayName}
                  </SecondarySectionTitle>
                </SectionHeader>
                <ThreeThreeVC cards={contentSection5Articles} />
              </SectionWithTitle>

              <SectionWithTitle>
                <SectionHeader linkText={contentSection6Text} href={contentSection6Link}>
                  <SecondarySectionTitle>
                    {contentSection6DisplayName}
                  </SecondarySectionTitle>
                </SectionHeader>
                <OneOnTopThreeHorizontalArticleList cards={contentSection6Articles} />
              </SectionWithTitle>

              <SectionWithTitle>
                <SectionHeader linkText={contentSection7Text} href={contentSection7Link}>
                  <SecondarySectionTitle>
                    {contentSection7DisplayName}
                  </SecondarySectionTitle>
                </SectionHeader>
                <ThreeHCOneOTC cards={contentSection7Articles} />
              </SectionWithTitle>

            </div>
            <Ad id={oneOnOneId} />
          </MainWrapperForHeaderWithSubMenus>
          <FooterConnected />
        </AppContent>
      </Theme>
    </React.Fragment>
  );
};
